import React, { useMemo } from 'react';
import { Badge, Tooltip } from '@fluentui/react-components';
import {
    CheckmarkCircle16Regular,
    DismissCircle16Regular,
    Warning20Regular,
    Info16Regular,
    DocumentText16Regular,
} from '@fluentui/react-icons';

const REPORT_CHECKS: Array<{
    field: string;
    label: string;      // short label for column header
    fullLabel: string;  // full label for tooltip
    invertLogic?: boolean;
    tooltip: string;
}> = [
        { field: 'writeForPeopleFirst', label: 'Human', fullLabel: 'People-First', tooltip: 'Written for humans, not AI bots' },
        { field: 'complexLayoutPenalized', label: 'Layout', fullLabel: 'Simple Layout', invertLogic: true, tooltip: 'Tag density is acceptable for AI parsing' },
        { field: 'shortSections', label: 'Sections', fullLabel: 'Short Sections', tooltip: 'No oversize (>450 token) chunks' },
        { field: 'clearHeadings', label: 'H2/H3', fullLabel: 'Clear Headings', tooltip: 'Proper semantic heading hierarchy exists' },
        { field: 'metadataPresent', label: 'Metadata', fullLabel: 'Metadata & Props', tooltip: 'Intent / page classification defined' },
        { field: 'imagesHaveAltText', label: 'Alt Text', fullLabel: 'Images Alt Text', tooltip: 'All images have descriptive alt text' },
        { field: 'hasStrongSummary', label: 'Summary', fullLabel: 'Page Summary', tooltip: 'Opening paragraph is a clear, sized summary' },
        { field: 'noKeywordStuffing', label: 'Keywords', fullLabel: 'No Stuffing', tooltip: 'Natural keyword density detected' }
    ];

const getUrlPath = (url: string): string => {
    try { return new URL(url).pathname; } catch { return url; }
};

const getUrlFilename = (url: string): string => {
    try {
        const pathname = new URL(url).pathname;
        return pathname.split('/').filter(Boolean).pop() ?? pathname;
    } catch { return url; }
};

export const CopilotDataTable: React.FC<{ items: any[] }> = ({ items }) => {
    const [sort, setSort] = React.useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'Readiness', dir: 'desc' });
    const [filter, setFilter] = React.useState('');
    const [page, setPage] = React.useState(0);
    const PAGE_SIZE = 20;

    const sorted = useMemo(() => {
        const filtered = items.filter(item => {
            if (!filter) return true;
            const q = filter.toLowerCase();
            const title = (item.Title || item.Name || '').toLowerCase();
            const path = item.webUrl ? getUrlPath(item.webUrl).toLowerCase() : '';
            return title.includes(q) || path.includes(q);
        });
        return filtered.sort((a, b) => {
            const av = a[sort.field] ?? 0;
            const bv = b[sort.field] ?? 0;
            const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
            return sort.dir === 'asc' ? cmp : -cmp;
        });
    }, [items, sort, filter]);

    const pageItems = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const totalPages = Math.ceil(sorted.length / PAGE_SIZE);

    const toggleSort = (field: string) => {
        setSort(s => ({ field, dir: s.field === field && s.dir === 'desc' ? 'asc' : 'desc' }));
        setPage(0);
    };

    const SortIcon = ({ field }: { field: string }) => (
        <span className="ml-0.5 text-gray-400 text-[10px]">
            {sort.field === field ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}
        </span>
    );

    return (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            {/* Table Header Bar */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-bold text-gray-900">Page-Level Breakdown</h3>
                    <p className="text-sm text-gray-500">
                        {sorted.length} of {items.length} pages
                        {sort.field && <span className="ml-1">· sorted by <strong>{sort.field}</strong></span>}
                    </p>
                </div>
                <input
                    type="text"
                    placeholder="Filter by page title…"
                    value={filter}
                    onChange={e => { setFilter(e.target.value); setPage(0); }}
                    className="px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ba6bb]/40 w-full sm:w-64"
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" style={{ minWidth: 900 }}>
                    <thead>
                        {/* Main column headers */}
                        <tr className="bg-gray-50 border-b border-gray-200 text-left">
                            <th rowSpan={2} className="px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-[#2ba6bb] border-r border-gray-200 align-bottom w-[240px]" onClick={() => toggleSort('Title')}>
                                Page Title <SortIcon field="Title" />
                            </th>
                            <th rowSpan={2} className="px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-[#2ba6bb] text-center border-r border-gray-200 align-bottom" onClick={() => toggleSort('Intent')}>
                                Intent <SortIcon field="Intent" />
                            </th>
                            <th rowSpan={2} className="px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-[#2ba6bb] text-center border-r border-gray-200 align-bottom" onClick={() => toggleSort('Readiness')}>
                                <Tooltip content="Composite AI Readiness score 0-100" relationship="label">
                                    <span>Readiness <Info16Regular className="inline-block -mt-0.5 text-gray-400" /></span>
                                </Tooltip>
                                <SortIcon field="Readiness" />
                            </th>
                            <th rowSpan={2} className="px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-[#2ba6bb] text-center border-r border-gray-200 align-bottom" onClick={() => toggleSort('Readability')}>
                                <Tooltip content="Flesch Reading Ease score (60+ is ideal)" relationship="label">
                                    <span>Readability <Info16Regular className="inline-block -mt-0.5 text-gray-400" /></span>
                                </Tooltip>
                                <SortIcon field="Readability" />
                            </th>
                            <th rowSpan={2} className="px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-[#2ba6bb] text-center border-r border-gray-200 align-bottom" onClick={() => toggleSort('StructureScore')}>
                                <Tooltip content="H2/H3 heading and list density score" relationship="label">
                                    <span>Structure <Info16Regular className="inline-block -mt-0.5 text-gray-400" /></span>
                                </Tooltip>
                                <SortIcon field="StructureScore" />
                            </th>
                            <th rowSpan={2} className="px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-[#2ba6bb] text-center border-r border-gray-200 align-bottom" onClick={() => toggleSort('MatrixQuadrant')}>
                                <Tooltip content="Readability Matrix classification (Best Practice, Needs Simplification, Needs Structure, Major Revision)" relationship="label">
                                    <span>Matrix <Info16Regular className="inline-block -mt-0.5 text-gray-400" /></span>
                                </Tooltip>
                                <SortIcon field="MatrixQuadrant" />
                            </th>
                            {/* Group header spanning all 8 check columns */}
                            <th colSpan={REPORT_CHECKS.length} className="px-4 py-2 font-semibold text-gray-600 text-center bg-[#e0f4f7]/40 border-b border-gray-200">
                                Content Quality Checks
                            </th>
                        </tr>
                        {/* Individual check column headers */}
                        <tr className="bg-[#e0f4f7]/20 border-b border-gray-200">
                            {REPORT_CHECKS.map(c => (
                                <th key={c.field} className="px-1 py-2 text-center border-r border-gray-100 last:border-0" style={{ minWidth: 40 }}>
                                    <Tooltip content={`${c.fullLabel}: ${c.tooltip}`} relationship="label">
                                        <span className="text-[10px] font-semibold text-[#2ba6bb] cursor-help whitespace-nowrap block leading-tight">
                                            {c.label}
                                        </span>
                                    </Tooltip>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.map((item, i) => {
                            const readiness = Number(item.Readiness) || 0;
                            const readColor = readiness > 80 ? 'text-emerald-700' : readiness >= 60 ? 'text-amber-600' : 'text-red-600';
                            const readBg = readiness > 80 ? 'bg-emerald-50' : readiness >= 60 ? 'bg-amber-50' : 'bg-red-50';
                            const report = item.AnalysisReport || {};
                            const hasReport = Object.keys(report).length > 0;

                            return (
                                <tr key={i} className={`hover:bg-[#e0f4f7]/25 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                                    <td className="px-3 py-2.5 border-r border-gray-100 w-[240px] max-w-[240px]">
                                        <a
                                            href={item.webUrl || '#'}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-medium text-[#2ba6bb] hover:text-[#186a78] hover:underline transition-colors truncate block text-sm leading-snug"
                                            title={item.Title || item.Name}
                                        >
                                            {item.Title || item.Name}
                                        </a>
                                        {item.webUrl && (
                                            <Tooltip content={getUrlPath(item.webUrl)} relationship="label">
                                                <span className="flex items-center gap-1 mt-0.5 w-fit cursor-default">
                                                    <span className="text-[11px] text-gray-400">
                                                        {getUrlFilename(item.webUrl)}
                                                    </span>
                                                    <DocumentText16Regular className="text-gray-300 shrink-0" style={{ width: 12, height: 12 }} />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-center border-r border-gray-100">
                                        <Badge appearance="tint" color="brand" shape="rounded" size="small">
                                            {item.Intent || 'General'}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-2.5 text-center border-r border-gray-100">
                                        <span className={`inline-block px-2 py-0.5 rounded-md font-bold text-sm ${readBg} ${readColor}`}>
                                            {readiness.toFixed(0)}%
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-center border-r border-gray-100 text-gray-700 font-medium text-sm">
                                        {(Number(item.Readability) || 0).toFixed(1)}
                                    </td>
                                    <td className="px-4 py-2.5 text-center border-r border-gray-100 text-gray-700 font-medium text-sm">
                                        {(Number(item.StructureScore) || 0).toFixed(1)}%
                                    </td>
                                    <td className="px-4 py-2.5 text-center border-r border-gray-100">
                                        <span className={`inline-block px-2 py-0.5 rounded-md font-bold text-[10px] whitespace-nowrap 
                                            ${item.MatrixQuadrant === 'Best Practice' ? 'bg-emerald-50 text-emerald-700'
                                                : item.MatrixQuadrant === 'Needs Simplification' ? 'bg-blue-50 text-blue-700'
                                                    : item.MatrixQuadrant === 'Needs Structure' ? 'bg-amber-50 text-amber-700'
                                                        : item.MatrixQuadrant === 'Major Revision' ? 'bg-red-50 text-red-700'
                                                            : 'bg-gray-100 text-gray-600'}`}>
                                            {item.MatrixQuadrant || 'Unknown'}
                                        </span>
                                    </td>
                                    {/* One <td> per check — each aligned exactly under its header */}
                                    {REPORT_CHECKS.map(c => {
                                        if (!hasReport) {
                                            return (
                                                <td key={c.field} className="px-2 py-2.5 text-center border-r border-gray-100 last:border-0">
                                                    <span className="text-gray-300 text-xs">—</span>
                                                </td>
                                            );
                                        }

                                        // Layout column: 3-state based on complexLayoutLevel
                                        if (c.field === 'complexLayoutPenalized') {
                                            const level = (report as any).complexLayoutLevel as string | undefined;
                                            if (!level || level === 'Excellent' || level === 'Standard') {
                                                return (
                                                    <td key={c.field} className="px-2 py-2.5 text-center border-r border-gray-100 last:border-0">
                                                        <Tooltip content={`Layout: ${level || 'Excellent'}`} relationship="label">
                                                            <CheckmarkCircle16Regular className="text-emerald-500 inline-block" />
                                                        </Tooltip>
                                                    </td>
                                                );
                                            }
                                            if (level === 'Warning') {
                                                return (
                                                    <td key={c.field} className="px-2 py-2.5 text-center border-r border-gray-100 last:border-0">
                                                        <Tooltip content="Layout: Warning, tag density 0.19–0.25" relationship="label">
                                                            <Warning20Regular className="text-amber-500 inline-block" style={{ width: 16, height: 16 }} />
                                                        </Tooltip>
                                                    </td>
                                                );
                                            }
                                            // Fail
                                            return (
                                                <td key={c.field} className="px-2 py-2.5 text-center border-r border-gray-100 last:border-0">
                                                    <Tooltip content="Layout: Fail, tag density >0.25" relationship="label">
                                                        <DismissCircle16Regular className="text-red-500 inline-block" />
                                                    </Tooltip>
                                                </td>
                                            );
                                        }

                                        // All other checks: standard boolean pass/fail
                                        const rawVal = report[c.field] as boolean;
                                        const passes = c.invertLogic ? !rawVal : rawVal;
                                        return (
                                            <td key={c.field} className="px-2 py-2.5 text-center border-r border-gray-100 last:border-0">
                                                {passes
                                                    ? <CheckmarkCircle16Regular className="text-emerald-500 inline-block" />
                                                    : <DismissCircle16Regular className="text-red-500 inline-block" />
                                                }
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                        Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:border-[#2ba6bb] hover:text-[#2ba6bb] transition-colors"
                        >
                            ← Previous
                        </button>
                        <span className="px-3 py-1.5 text-sm text-gray-500 font-medium">{page + 1} / {totalPages}</span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={page === totalPages - 1}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:border-[#2ba6bb] hover:text-[#2ba6bb] transition-colors"
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}

            {items.length === 0 && (
                <div className="text-center p-12 text-gray-400 flex flex-col items-center gap-2">
                    <Warning20Regular className="w-8 h-8 text-gray-300" />
                    <span className="text-sm">No pages processed yet. Initializing ML worker…</span>
                </div>
            )}
        </div>
    );
};
